import { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Flex } from "flicket-ui";
import { AnimatePresence } from "framer-motion";
import { useRouter } from "next/router";
import { ContentModal, Layout, Seo, Status } from "~components";
import {
  Actions,
  FilterModal,
  Filters,
  MobileFilters,
  Options,
  SeatsIO,
} from "~components/reservation";
import { MobileOptions } from "~components/reservation/options/options.MobileLayout";
import { Placeholder } from "~components/reservation/options/options.Placeholder";
import { TicketDetailsModal } from "~features/seated-reservation/components/common/modal.TicketDetails";
import { LandingPage, VenueSeating } from "~graphql/sdk";
import {
  useAccount,
  useIsMobile,
  useOrganization,
  useReservation,
} from "~hooks";
import { showToast } from "~lib";
import { useAtom } from "jotai";
import { googleAnalyticsMembershipAtom } from "~hooks/useGoogleAnalyticsPageView";
import { MembershipVenue } from "~features/reservation/reservation-types";
import { NonSeatedMembershipReservation } from "~features/nonseated-reservation/components/NonSeatedMembershipReservation";
import { initialState } from "~features/seated-reservation/store";
import EventHeader from "~components/common/common.EventHeader";
import { OptionalSeekaProvider } from "~lib/seeka";

// Temp for ASB Classic.
// Remove after Feb 2025
const OPTIONS = new Map<
  string,
  { showOptions: boolean; minQty: number; maxQty: number }
>([
  [
    "12c577cf-dd2f-49e1-a795-afff92bb851d",
    { showOptions: false, minQty: 6, maxQty: 6 },
  ],
  [
    "6af4a59f-22d5-4467-a34f-d7e6ca347334",
    { showOptions: false, minQty: 6, maxQty: 6 },
  ],
  [
    "aa5850f9-3dc3-4a30-a628-d2e3d677353d",
    { showOptions: false, minQty: 6, maxQty: 6 },
  ],
  [
    "3083875b-6e43-4097-8921-f2509d2cabf9",
    { showOptions: false, minQty: 6, maxQty: 6 },
  ],
]);

const MembershipReservation = () => {
  const router = useRouter();
  const membershipId = router.query.membershipId as string;
  const membershipOptions = OPTIONS.get(membershipId);
  const { organization, hasFeature, t } = useOrganization();
  const [isNavigating, setNavigating] = useState(false);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [onAddError, setOnAddError] = useState("");
  const isMobile = useIsMobile();
  const [showOptions, setShowOptions] = useState(
    membershipOptions?.showOptions ?? !router?.query?.ownedTickets
  );
  const { isAdmin } = useAccount();
  const [eventVenues, setEventVenues] = useState<MembershipVenue>([]);
  const [locations, setLocations] = useState("");

  const {
    chart,
    membership,
    error,
    isLoading,
    handleOrderCreation,
    options,
    selectableZones,
    updateOptions,
    getPrice,
    changeTickets,
    isLoadingZones,
    priceComp,
    seatAmountOptions,
    chartReady,
  } = useReservation("membership", membershipId);

  const isInactive = membership?.isActive === false;

  useEffect(() => {
    if (isInactive) {
      void router.push("/memberships");
    }
  }, [isInactive]);

  const [, setMembership] = useAtom(googleAnalyticsMembershipAtom);

  useEffect(() => {
    if (membership) {
      //  @ts-expect-error atom type issue while using strict:false
      setMembership(membership);
    }
  }, [membership]);

  const changeCoverType = (value: string) => updateOptions("coverType", value);
  const changeZoneType = (value: string) => updateOptions("zoneType", value);
  const changeSort = (value: string) => updateOptions("selectedSort", value);
  const setAmount = (value: number) => updateOptions("amount", value);
  const setZone = (value: string) => updateOptions("selectedZone", value);
  const setSection = (value: string) => updateOptions("selectedSection", value);
  const changeSeatSelect = (value: string) =>
    updateOptions("seatSelectType", value);

  let seatSelectAmount = options.amount ?? initialState.options.amount;
  let seatOptionsFiltered = [...seatAmountOptions];

  if (membershipOptions) {
    seatOptionsFiltered = seatAmountOptions.filter(
      (s) =>
        s.value >= membershipOptions.minQty &&
        s.value <= membershipOptions.maxQty
    );

    if (membershipOptions.minQty > seatSelectAmount) {
      seatSelectAmount = membershipOptions.minQty;
    }
  }

  useEffect(() => {
    if (
      !options?.validSeatSelection &&
      seatSelectAmount >= 0 &&
      membership?.venue?.seating === VenueSeating.Seated
    ) {
      showToast(
        "Invalid seat selection. You have left a single seat empty",
        "error"
      );
    }
  }, [options?.validSeatSelection]);

  useEffect(() => {
    if (chartReady && seatSelectAmount) {
      setAmount(seatSelectAmount);
    }
  }, [chartReady]);

  const onNavigateCheckout = useCallback(async () => {
    if (isNavigating) return;

    setNavigating(true);

    const wasSuccessful = await handleOrderCreation((errorMessage) => {
      setOnAddError(errorMessage);
      setIsErrorModalOpen(true);
      setNavigating(false);
      return;
    });

    if (!wasSuccessful) {
      setNavigating(false);
    }
  }, [handleOrderCreation]);

  const handleClose = () => {
    setShowOptions(false);
  };

  useEffect(() => {
    membership?.events.forEach((membershipEvent) => {
      const eventVenueList = eventVenues;
      eventVenueList.push(membershipEvent.venue);

      const newlist = Array.from(
        new Set(eventVenueList.map((venue) => venue.name))
      ).map((name) => {
        return {
          name: name,
          address: eventVenueList.find((venue) => venue.name === name).address,
          timezone: eventVenueList.find((venue) => venue.name === name)
            .timezone,
          locale: eventVenueList.find((venue) => venue.name === name).locale,
        };
      });
      setEventVenues(newlist);
      setLocations(
        newlist
          .map((venue) => `${venue.name}, ${venue?.address?.city}`)
          .join(", ")
      );
    });
  }, [membership]);

  //For seated memberships
  const memoizedActions = useMemo(
    () => (
      <Actions
        membership={membership}
        addons={membership?.addons}
        changeTickets={changeTickets}
        changingSeatsFee={membership?.changingSeatsFee}
        venueName={membership?.venue?.name}
        zones={selectableZones}
        currentZone={options?.selectedZone}
        setCurrentZone={setZone}
        validSeatSelection={options?.validSeatSelection}
        currentSort={options?.selectedSort}
        currentSection={options?.selectedSection}
        setCurrentSection={setSection}
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        navigateCheckout={onNavigateCheckout}
        isNavigating={isNavigating}
        getPrice={getPrice}
        priceComp={priceComp}
      />
    ),
    [
      membership?.addons,
      membership?.venue?.name,
      membership?.changingSeatsFee,
      changeTickets,
      selectableZones,
      setZone,
      setSection,
      options?.selectedSort,
      options?.selectedZone,
      options?.selectedSection,
      onNavigateCheckout,
      isNavigating,
      getPrice,
      priceComp,
      options?.validSeatSelection,
    ]
  );

  const memoizedSeatsIO = useMemo(
    () => <SeatsIO showOverlay={showOptions} chart={chart} />,
    [chart]
  );

  const loading = isLoading || !membership?.venue?.seating || isInactive;

  const defaultTitle = `Buy ${t("membership_name")}${
    membership?.name ? ` - ${membership?.name}` : ""
  }`;

  if (
    !loading &&
    !hasFeature("LegacyGATicketSelection") &&
    membership?.venue?.seating !== VenueSeating.Seated
  ) {
    return (
      <>
        <Seo title={defaultTitle} description={undefined} />
        <NonSeatedMembershipReservation
          membership={membership}
          isMembership
          isAdmin={isAdmin}
        />
      </>
    );
  }

  return (
    <>
      <Seo title={defaultTitle} description={undefined} />
      <Status error={error} loading={loading}>
        <OptionalSeekaProvider seekaConfig={membership?.seekaConfig}>
          <EventHeader
            type="membership"
            title={membership?.name}
            startDate={membership?.startDate}
            endDate={membership?.endDate}
            venue={membership?.venue}
            location={locations}
            accessibility={membership?.accessibility}
            image={membership?.banner}
            description={membership?.description}
            supportInfo={organization?.supportInfo}
            goBack={() =>
              void router.push(
                organization?.landingPage === LandingPage.SingleEvent
                  ? "/"
                  : "/memberships"
              )
            }
            coordinates={`${membership?.venue?.location?.lat},${membership?.venue?.location?.lng}`}
          />
          <Layout layoutType="sub">
            {membership?.venue?.seating !== VenueSeating.Seated ? (
              <NonSeatedMembershipReservation
                membership={membership}
                isMembership
                isAdmin={isAdmin}
              />
            ) : (
              <>
                <Filters
                  amount={seatSelectAmount}
                  setAmount={setAmount}
                  amountDisabled={!!changeTickets?.length}
                  coverType={options?.coverType}
                  setCoverType={changeCoverType}
                  alcoholFreeZone={options?.zoneType}
                  setAlcoholFreeZone={changeZoneType}
                  selectedSort={options?.selectedSort}
                  setSelectedSort={changeSort}
                  seatSelectType={options?.seatSelectType}
                  setSeatSelectType={changeSeatSelect}
                  seatAmountOptions={seatOptionsFiltered}
                  filterOptions={membership?.venue?.filterOptions}
                />
                <Flex
                  justifyContent={["flex-end", "flex-start"]}
                  overflow="hidden"
                  position="relative"
                  height={
                    showOptions
                      ? {
                          _: isAdmin
                            ? "calc(100vh - 96px)"
                            : "calc(100vh - 64px)",
                        }
                      : {
                          _: isAdmin
                            ? "calc(100vh - 96px)"
                            : "calc(100vh - 64px)",
                          md: isAdmin
                            ? "calc(100vh - 169px)"
                            : "calc(100vh - 122px)",
                        }
                  }
                  maxHeight={
                    showOptions
                      ? {
                          _: isAdmin
                            ? "calc(100vh - 96px)"
                            : "calc(100vh - 64px)",
                        }
                      : {
                          _: isAdmin
                            ? "calc(100vh - 96px)"
                            : "calc(100vh - 64px)",
                          md: isAdmin
                            ? "calc(100vh - 169px)"
                            : "calc(100vh - 122px)",
                        }
                  }
                >
                  <Flex w="100%" maxW="100%">
                    {!isMobile && (
                      <>
                        <Box
                          position="relative"
                          d={{ _: "none", md: "block" }}
                          width="100%"
                          minW={{ _: "100%", md: "256px" }}
                          maxW={{ _: "100%", md: "403px" }}
                        >
                          {isLoadingZones ||
                          typeof selectableZones === "undefined" ? (
                            <Placeholder />
                          ) : (
                            memoizedActions
                          )}
                        </Box>
                        {showOptions && (
                          <AnimatePresence>
                            <FilterModal isOpen={true} onClose={handleClose}>
                              <Options
                                amount={seatSelectAmount}
                                handleClose={handleClose}
                                setAmount={setAmount}
                                seatAmountOptions={seatOptionsFiltered}
                              />
                            </FilterModal>
                          </AnimatePresence>
                        )}
                      </>
                    )}
                    <Box position="relative" maxW="100%" width="100%" bg="N100">
                      {memoizedSeatsIO}
                      <MobileFilters
                        amount={seatSelectAmount}
                        setAmount={setAmount}
                        amountDisabled={!!changeTickets?.length}
                        coverType={options?.coverType}
                        setCoverType={changeCoverType}
                        alcoholFreeZone={options?.zoneType}
                        setAlcoholFreeZone={changeZoneType}
                        selectedSort={options?.selectedSort}
                        setSelectedSort={changeSort}
                        seatSelectType={options?.seatSelectType}
                        setSeatSelectType={changeSeatSelect}
                        seatAmountOptions={seatOptionsFiltered}
                        filterOptions={membership?.venue?.filterOptions}
                      />
                      {isLoadingZones ||
                      typeof selectableZones === "undefined" ? (
                        <Placeholder />
                      ) : (
                        memoizedActions
                      )}
                    </Box>
                  </Flex>
                  <MobileOptions showOptions={showOptions}>
                    <Options
                      amount={seatSelectAmount}
                      handleClose={handleClose}
                      setAmount={setAmount}
                      seatAmountOptions={seatOptionsFiltered}
                    />
                  </MobileOptions>
                </Flex>
              </>
            )}
            <TicketDetailsModal />
          </Layout>

          <ContentModal
            isOpen={isErrorModalOpen}
            setIsOpen={() => setIsErrorModalOpen(false)}
            title="There was a problem..."
          >
            <div dangerouslySetInnerHTML={{ __html: onAddError }}></div>
          </ContentModal>
        </OptionalSeekaProvider>
      </Status>
    </>
  );
};

export default MembershipReservation;
